import { useRef, useMemo, useEffect, memo } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Stars } from "@react-three/drei";
import * as THREE from "three";

// ── Particle Field — 800 gold/violet points ────────────────────────────────
function ParticleField({ isDark }) {
  const ref = useRef();
  const COUNT = 800;

  const { positions, colors } = useMemo(() => {
    const positions = new Float32Array(COUNT * 3);
    const colors = new Float32Array(COUNT * 3);
    const gold = new THREE.Color(isDark ? 0xf0c040 : 0xb45309);
    const violet = new THREE.Color(isDark ? 0x8b5cf6 : 0x7c3aed);

    for (let i = 0; i < COUNT; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 100;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 100;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 60;
      const c = gold.clone().lerp(violet, Math.random());
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }
    return { positions, colors };
  }, [isDark]);

  useFrame((state) => {
    if (ref.current) {
      // Slow drift rotation — arcane, contemplative
      ref.current.rotation.y = state.clock.elapsedTime * 0.018;
      ref.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.008) * 0.05;
    }
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          array={positions}
          count={COUNT}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          array={colors}
          count={COUNT}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.28}
        vertexColors
        transparent
        opacity={isDark ? 0.55 : 0.45}
        sizeAttenuation
      />
    </points>
  );
}

// ── Network Connection Lines — dim arcane threads ──────────────────────────
function NetworkLines({ isDark }) {
  const lineColor = isDark ? 0x8b5cf6 : 0x7c3aed;

  // Generate 40 node positions once; only connect close pairs
  const lines = useMemo(() => {
    const nodes = Array.from(
      { length: 40 },
      () =>
        new THREE.Vector3(
          (Math.random() - 0.5) * 80,
          (Math.random() - 0.5) * 60,
          (Math.random() - 0.5) * 30,
        ),
    );
    const pairs = [];
    nodes.forEach((a, i) =>
      nodes.slice(i + 1).forEach((b) => {
        if (a.distanceTo(b) < 18) pairs.push([a.clone(), b.clone()]);
      }),
    );
    return pairs;
  }, []);

  return (
    <group>
      {lines.map(([a, b], i) => (
        <line key={i}>
          <bufferGeometry onUpdate={(geo) => geo.setFromPoints([a, b])} />
          <lineBasicMaterial
            color={lineColor}
            transparent
            opacity={isDark ? 0.07 : 0.05}
          />
        </line>
      ))}
    </group>
  );
}

// ── Central Polyhedra — rotating Platonic solids ───────────────────────────
// Dodecahedron = symbol of the cosmos (Plato's fifth element, the universe)
// Octahedron   = symbol of air / intellect
function CentralPolyhedra({ isDark }) {
  const dodecaRef = useRef();
  const octaRef = useRef();

  const goldColor = isDark ? "#f0c040" : "#b45309";
  const violetColor = isDark ? "#8b5cf6" : "#7c3aed";

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (dodecaRef.current) {
      dodecaRef.current.rotation.y = t * 0.12;
      dodecaRef.current.rotation.x = t * 0.07;
    }
    if (octaRef.current) {
      octaRef.current.rotation.y = -t * 0.1;
      octaRef.current.rotation.z = t * 0.06;
    }
  });

  return (
    <>
      {/* Central dodecahedron — golden, symbol of cosmic order */}
      <mesh ref={dodecaRef} position={[0, 0, 0]}>
        <dodecahedronGeometry args={[9, 0]} />
        <meshBasicMaterial
          color={goldColor}
          wireframe
          transparent
          opacity={isDark ? 0.22 : 0.18}
        />
      </mesh>

      {/* Orbiting octahedron — violet, symbol of intellect */}
      <mesh ref={octaRef} position={[18, -6, -12]}>
        <octahedronGeometry args={[5, 0]} />
        <meshBasicMaterial
          color={violetColor}
          wireframe
          transparent
          opacity={isDark ? 0.28 : 0.22}
        />
      </mesh>

      {/* Secondary small icosahedron for variety */}
      <SmallIcosa isDark={isDark} />
    </>
  );
}

function SmallIcosa({ isDark }) {
  const ref = useRef();
  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.x = state.clock.elapsedTime * 0.15;
      ref.current.rotation.z = state.clock.elapsedTime * 0.09;
    }
  });
  return (
    <mesh ref={ref} position={[-20, 8, -8]}>
      <icosahedronGeometry args={[3.5, 0]} />
      <meshBasicMaterial
        color={isDark ? "#67e8f9" : "#0891b2"}
        wireframe
        transparent
        opacity={isDark ? 0.2 : 0.15}
      />
    </mesh>
  );
}

// ── Mouse Parallax ─────────────────────────────────────────────────────────
// Reads window mouse position and smoothly nudges camera, matching portfolio behavior
function ParallaxCamera() {
  const { camera } = useThree();
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const onMove = (e) => {
      mouse.current.x = (e.clientX / window.innerWidth - 0.5) * 2;
      mouse.current.y = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  useFrame(() => {
    camera.position.x += (mouse.current.x * 4 - camera.position.x) * 0.025;
    camera.position.y += (-mouse.current.y * 3 - camera.position.y) * 0.025;
  });

  return null;
}

// ── ArcaneBackground — exported root component ─────────────────────────────
// Fixed-position, pointer-events none so it never blocks UI interactions.
// Entirely re-mounts when isDark changes (key prop) so colors update cleanly.
const ArcaneBackground = memo(function ArcaneBackground({ isDark }) {
  return (
    <Canvas
      key={isDark ? "dark" : "light"}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 0,
        pointerEvents: "none",
      }}
      camera={{ fov: 75, near: 0.1, far: 1000, position: [0, 0, 30] }}
      gl={{ alpha: true, antialias: true }}
      dpr={[1, 2]}
    >
      {/* Distant star field — drei helper */}
      <Stars
        radius={120}
        depth={60}
        count={2500}
        factor={3}
        saturation={0}
        fade
        speed={0.4}
      />

      <ParticleField isDark={isDark} />
      <NetworkLines isDark={isDark} />
      <CentralPolyhedra isDark={isDark} />
      <ParallaxCamera />
    </Canvas>
  );
});

export default ArcaneBackground;
